import { useMemo } from "react"

import { useTranslation } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import Callout from "components/TextElements/Callout"
import colors from "styles/colors"
import text, { transparentText } from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useMedia from "utils/useMedia"

export default function Glance() {
  const titleWidth = useMedia("883px", "53.193vw", "100%", "100%")
  const card1Offset = useMedia("0", "0", "0", "")
  const card2Offset = useMedia("140px", "8.434vw", "8.984vw", "")
  const card3Offset = useMedia("90px", "5.422vw", "5.762vw", "")
  const card4Offset = useMedia("190px", "11.446vw", "12.207vw", "")
  const card5Offset = useMedia("40px", "2.41vw", "2.539vw", "")
  const innerWidth = useMedia("1015px", "61.145vw", "81.445vw", "89.333vw")
  const { t } = useTranslation()

  const allCards = useMemo(() => {
    const cardData = [
      {
        stat: t("100+"),
        underline: t("Best-In-Class Platform Partners"),
        color: colors.turqSeaVert,
        offset: card1Offset,
      },
      {
        stat: ("168M"),
        underline: t("Accounts"),
        color: colors.blueBabyHorz,
        offset: card2Offset,
      },
      {
        stat: t("2"),
        underline: t("Decades of Experience"),
        color: colors.plumHorz,
        offset: card3Offset,
      },
      {
        stat: t("1.7k"),
        underline: t("Team Members"),
        color: colors.magCoralHorz,
        offset: card4Offset,
      },
      {
        stat: t("14"),
        underline: t("Supported Countries"),
        color: colors.turqBlueHorz,
        offset: card5Offset,
      },
    ]

    return cardData.map(card => {
      return (
        <NumberLine key={`number-${card.stat}`}>
          <Stat color={card.color}>{card.stat}</Stat>{" "}
          <Label>{card.underline}</Label>
        </NumberLine>
      )
    })
  }, [card1Offset, card2Offset, card3Offset, card4Offset, card5Offset, t])

  return (
    <Wrapper>
      <InnerWrapper setWidth={innerWidth}>
        <Callout
          title={{
            content: t("Galileo at a glance."),
            width: titleWidth,
            color: colors.blue800,
          }}
          kicker={{
            text: t("Our Track Record") ?? "Our Track Record",
            icon: "layers",
            color: colors.blue200,
          }}
          centered
        />
        <CardsWrapper>{allCards}</CardsWrapper>
      </InnerWrapper>
    </Wrapper>
  )
}

const NumberLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-top: 1px solid ${colors.blue50};
  width: 100%;

  ${fresponsive(css`
    padding-top: 25px;
    margin-bottom: 50px;
    min-height: 251px;
  `)}

  ${ftablet(css`
    padding-top: 10px;
    margin-bottom: 40px;
    min-height: 185px;
  `)}

  ${fmobile(css`
    width: 335px;
    height: 78px;
    padding-top: 10px;
    margin-bottom: 20px;
    min-height: auto;
  `)}
`

const Stat = styled.p<{ color: string }>`
  position: relative;
  ${text.h4};

  ${transparentText};
  background-image: ${({ color }) => color};
  line-height: 80%;

  ${fresponsive(css`
    font-size: 250px;
  `)}

  ${ftablet(css`
    font-size: 144px;
    line-height: 90%;
  `)}
  ${fmobile(css`
    ${text.h3};
    line-height: 90%;
  `)}
`

const Label = styled.p`
  ${text.h5};
  color: ${colors.blue800};
  display: flex;
  flex-wrap: wrap;
  text-align: left;

  ${fresponsive(css`
    width: 350px;
    margin-bottom: 10px;
  `)}

  ${ftablet(css`
    width: 260px;
  `)}

  ${fmobile(css`
    ${text.bodyXS};
    width: 120px;
    margin-bottom: 10px;
  `)}
`

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  will-change: transform;
  transform: translate3d(0, 0, 0, 0);

  height: auto;
  overflow: hidden;

  ${fresponsive(css`
    padding: 150px 0 148px 0;
  `)}

  ${ftablet(css`
    padding: 85px 0 0 0;
  `)}

  ${fmobile(css`
    padding-bottom: 100px;
  `)}
`

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;

  ${fresponsive(css`
    margin-top: 150px;
  `)}

  ${ftablet(css`
    margin-top: 82px;
  `)}

  ${fmobile(css`
    justify-content: center;
    align-items: center;
    margin-top: 40px;
  `)}
`
